import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { arrivalCopy } from '@/features/visits/checkIn'
import { dayAt } from '@/features/visits/studioClock'

/**
 * What the card's foot holds: one thing, or nothing.
 *
 * The slot used to be a button row that also, on the day, became a QR code — two jobs in one
 * place, and the page's height moved every time a visit changed state. Now there is one slot and
 * exactly one of these is in it, chosen by the first test that passes:
 *
 *     CHECKED_IN     the receipt
 *     ENABLED        I'm Here, live
 *     EXPIRED        I'm Here, dark, with the desk
 *     isConfirmable  Confirm, live
 *     !isConfirmed   Confirm, dark, with when the window opens
 *     otherwise      I'm Here, dark, with when the door opens
 *
 * Arriving is tested before confirming on purpose. The two windows cannot overlap today — one
 * closes four days out, the other opens thirty minutes before — but a member standing in a lobby
 * having ignored every reminder wants the door, not a question from four days ago, and the server
 * treats their arrival as the confirmation anyway. Ordered this way, the day a window moves it is
 * still the live thing that wins.
 *
 * Both confirm branches are skipped entirely where the server says no confirmation is wanted —
 * `confirmationRequired: false`, which is provider-kapi#1167's switch saying nothing gets released
 * for silence. Then the slot is the door for the whole life of the visit: dark, naming when it
 * opens, live for the last thirty minutes. Asking for something with no consequence is how an app
 * teaches people to ignore it.
 */
export type VisitSlotState =
	| { kind: 'arrived' }
	/**
	 * The window has shut. Not an `arrive` that cannot be pressed: what is offered here is a
	 * different thing entirely — a way to reach a person — and a dead "I'm Here" was the wrong
	 * last word for somebody standing in a lobby fifteen minutes late.
	 */
	| { kind: 'closed' }
	| { kind: 'arrive'; label: string; enabled: boolean; helper?: string }
	| { kind: 'confirm'; label: string; enabled: boolean; helper?: string }

export const visitSlotState = (visit: UpcomingVisit): VisitSlotState => {
	const checkIn = visit.checkIn

	if (checkIn && checkIn.status !== 'NOT_YET_OPEN') {
		if (checkIn.status === 'CHECKED_IN') {
			return { kind: 'arrived' }
		}
		if (checkIn.status === 'EXPIRED') {
			return { kind: 'closed' }
		}
		return { kind: 'arrive', ...arrivalCopy(visit, checkIn) }
	}

	// Absent on a payload that predates #1167, and read as asking: that is how Wally has always
	// worked, so the older server's silence means the same as its yes.
	if (visit.confirmationRequired ?? true) {
		if (visit.isConfirmable) {
			return { kind: 'confirm', label: 'Confirm', enabled: true }
		}

		if (!visit.isConfirmed) {
			// The label names the day the button wakes up. A disabled control that does not say
			// why it is disabled sends a member looking for the reason, and this was the only
			// thing on the page with nothing to say for itself.
			return {
				kind: 'confirm',
				// Sentence case after the verb. "Confirm From Fri, Sep 25" is title case applied to
				// a label that is not a title but a fragment naming a moment, and the capital
				// makes the preposition read as another word to take in rather than the hinge
				// between the two halves.
				label: `Confirm from ${dayAt(visit, visit.confirmationOpensAt)}`,
				enabled: false,
			}
		}
	}

	// Confirmed, and the door is the next thing anyone will ask of them. Where the payload
	// predates the check-in block there is no door to name, so the slot says nothing and the card
	// ends at Move It.
	return checkIn
		? { kind: 'arrive', ...arrivalCopy(visit, checkIn) }
		: { kind: 'arrive', label: "I'm Here", enabled: false }
}
